import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { AdminCarDriver } from "../../http/carDrivers/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarCurrentAssignment, CarNoCarDepency } from "../../http/cars/types";
import { Manufacturer } from "../../http/manufacturers/types";
import { CarAssignmentType, getAssignmentTypes, OtherCarAssignment } from "../../http/otherCarAssignment/types";
import { Entity } from "../../types";
import { formatDateNumeric, getWarningBoxTitle } from "../../types/utils";
import { WarningBox } from "../ui/Boxes";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = [
  "id",
  "vehicleIdentificationNumber",
  "currentPlateNumber",
  "lastPlateNumber",
  "manufacturer",
  "model",
  "registrationDate",
] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

type car = Entity & {
  vehicleIdentificationNumber: string;
  manufacturer: Manufacturer;
  currentPlateNumber?: string;
  lastPlateNumber?: string;
  registrationDate?: string;
  model: string;
};
export interface OtherCarAssignmentFormBody {
  id: string;
  car: car;
  type: CarAssignmentType;
  costCenter: string;
  startDate: string;
  endDate?: string;
  prevEndDate?: string;
}

type OtherCarAssignmentFormProps = {
  otherCarAssignment?: OtherCarAssignment | null;
  onSubmit: (form: OtherCarAssignmentFormBody, carDriver?: AdminCarDriver) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
  type?: string;
};

const OtherCarAssignmentForm: FC<OtherCarAssignmentFormProps> = ({
  otherCarAssignment,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
  type,
}: OtherCarAssignmentFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const aborter = useRef(new AbortController());

  const types = getAssignmentTypes(i18n.t);

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [currentAssignment, setCurrentAssigment] = useState<CarCurrentAssignment>();

  const loadCurrentAssigmment = async (carId: string) => {
    const currentAssignment = await carsApi.getCurrentAssignment(carId, aborter.current.signal);
    setCurrentAssigment({
      carDriver: currentAssignment.carDriver,
      otherCarAssignment:
        currentAssignment.otherCarAssignment?.id !== otherCarAssignment?.id
          ? currentAssignment.otherCarAssignment
          : undefined,
    });
  };

  const defaultValues = {
    car: otherCarAssignment?.car,
    prevEndDate: currentAssignment?.carDriver?.endDate ?? currentAssignment?.otherCarAssignment?.endDate,
    startDate: otherCarAssignment?.startDate,
    endDate: otherCarAssignment?.endDate,
    costCenter: otherCarAssignment?.costCenter,
    type: types.find((z) => z.value == otherCarAssignment?.type) || types.find((z) => z.value == type),
  };

  useEffect(() => {
    const id = carId ?? otherCarAssignment?.car.id;
    id && loadCurrentAssigmment(id);
  }, [carId, otherCarAssignment?.car.id]);

  return (
    <Form<OtherCarAssignmentFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => (formData) => onSubmit(formData, currentAssignment?.carDriver)}
      itemCreationInfo={otherCarAssignment}
    >
      {({ register, formState: { errors }, reset, control, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [otherCarAssignment, currentAssignment]);

        const prevStartDate =
          currentAssignment?.carDriver?.startDate ?? currentAssignment?.otherCarAssignment?.startDate;
        const startDate = watch("startDate");

        // if startDate is before the already assigned driver/carPool startDate, the endDate is required
        const endDateRequired =
          (currentAssignment?.carDriver && startDate < currentAssignment.carDriver?.startDate) ||
          (currentAssignment?.otherCarAssignment && startDate < currentAssignment.otherCarAssignment?.startDate);

        // if there is an already assigned driver/carPool and the endDate is not required, the toDate is required
        const toDateRequired =
          !!(currentAssignment?.carDriver || currentAssignment?.otherCarAssignment) && !endDateRequired;

        const t = watch("type");
        useEffect(() => {
          const cc = currentAssignment?.carDriver?.driver.currentCostCenter ?? "";
          if (((!!t && t.value == "not-in-use") || (!!type && type == "not-in-use")) && !otherCarAssignment) {
            setValue("costCenter", cc);
          }
          if (((!!t && t.value == "car-pool") || (!!type && type == "car-pool")) && !otherCarAssignment) {
            setValue("costCenter", "91200");
          }
        }, [t, type, currentAssignment]);

        return (
          <FormFieldsContainer>
            {!!currentAssignment?.carDriver && (
              <>
                <WarningBox text={i18n.t.common.currentlyAssignToDriver} />
                <FormField label={i18n.t.common.currentDriver}>
                  <Input disabled value={currentAssignment.carDriver?.driver.fullName} />
                </FormField>
                <FormField label={i18n.t.common.from}>
                  <Input
                    disabled
                    value={formatDateNumeric(currentAssignment.carDriver?.startDate, i18n.language) ?? ""}
                  />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: !!toDateRequired,
                    validate: (value) => {
                      if (value && currentAssignment?.carDriver?.startDate) {
                        if (value === currentAssignment.carDriver.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (value < currentAssignment.carDriver.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error} isOptional={!toDateRequired}>
                      {({ isInvalid }) => (
                        <DatePicker
                          disabled={isDisabled || isLoading || !!otherCarAssignment}
                          isInvalid={isInvalid}
                          defaultValue={currentAssignment.carDriver?.endDate}
                          value={watch("prevEndDate")}
                          onChange={(v) => setValue("prevEndDate", v!)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            {!!currentAssignment?.otherCarAssignment && (
              <>
                <WarningBox text={getWarningBoxTitle(i18n.t, currentAssignment.otherCarAssignment.type)} />
                <FormField label={i18n.t.common.from}>
                  <Input
                    disabled
                    value={formatDateNumeric(currentAssignment.otherCarAssignment?.startDate, i18n.language) ?? ""}
                  />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: !!toDateRequired,
                    validate: (value) => {
                      if (value && currentAssignment?.otherCarAssignment) {
                        if (value === currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (value < currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error} isOptional={!toDateRequired}>
                      {({ isInvalid }) => (
                        <DatePicker
                          isInvalid={isInvalid}
                          disabled={isDisabled || isLoading || !!otherCarAssignment}
                          value={watch("prevEndDate")}
                          onChange={(v) => setValue("prevEndDate", v!)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            {!carId && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          isLoading={isLoading}
                          isDisabled={!!otherCarAssignment || isDisabled || isLoading}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber ||
                            x.lastPlateNumber ||
                            x.vehicleIdentificationNumber ||
                            `${x.manufacturer.name} ${x.model} ${
                              x.registrationDate && formatDateNumeric(x.registrationDate, i18n.language)
                            }`
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<CarSearch>(isInvalid)}
                          onChange={(v) => {
                            v ? loadCurrentAssigmment(v?.id) : setCurrentAssigment(undefined);
                            field.onChange(v);
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            {!type && (
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.type} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={types}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<CarAssignmentType>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
            <FormField label={i18n.t.common.costCenter} error={errors.costCenter}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("costCenter", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (value) {
                    const prevEndDate = watch("prevEndDate");
                    if (prevStartDate) {
                      if (prevEndDate) {
                        if (value >= prevStartDate && value < prevEndDate) {
                          return i18n.t.common.startDateFallsInCurrentAssignmentPeriod;
                        }
                        // } else {
                        //   if (value >= prevStartDate) {
                        //     return i18n.t.common.startDateMustBeBeforeCurrentAssignmentStartDate;
                        //   }
                      }
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      value={watch("startDate")}
                      onChange={(v) => !!v && setValue("startDate", v)}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: !!endDateRequired,
                validate: (value) => {
                  if (value) {
                    if (value === startDate) {
                      return i18n.t.common.endDateCantMatchStartDate;
                    }
                    if (value < startDate) {
                      return i18n.t.common.endDateBeforeStartDate;
                    }

                    const prevEndDate = watch("prevEndDate");
                    if (!prevEndDate && prevStartDate && value > prevStartDate) {
                      return i18n.t.common.endDateAfterCurrentAssignmentStartDate;
                    }
                    if (prevEndDate && prevStartDate && value > prevStartDate && value < prevEndDate) {
                      return i18n.t.common.endDateFallsInCurrentAssignmentPeriod;
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional={!endDateRequired}>
                  {({ isInvalid }) => (
                    <DatePicker
                      value={watch("endDate")}
                      onChange={(v) => setValue("endDate", v!)}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      clearable={!endDateRequired}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default OtherCarAssignmentForm;
